import {
  careNavigationTeamId,
  type CareNavigationTeamId,
  type CareNavigationStaffStatus,
} from "./careNavigationTeams";
import {
  CARE_NAVIGATION_CRITERIA,
  DEFAULT_CARE_NAVIGATION_CRITERIA,
  resolveCareNavigationCriteria,
  type CareNavigationCriteria,
  type CareNavigationDefinitionResponse,
} from "./careNavigationCriteria";

export type CareNavigationDefinitionState = {
  criteria: CareNavigationCriteria;
  month: string;
  navigators: string[];
  team?: CareNavigationTeamId;
  staffStatus?: CareNavigationStaffStatus;
};

export function readDefinitionState(
  search: string,
  currentMonth: string,
): CareNavigationDefinitionState {
  const params = new URLSearchParams(search);
  const overrides: Record<string, boolean> = {};
  for (const { id } of CARE_NAVIGATION_CRITERIA) {
    const value = params.get(`criterion.${id}`);
    if (value === "1" || value === "0") overrides[id] = value === "1";
  }
  const month = params.get("month") ?? "";
  const team = careNavigationTeamId(params.get("team"));
  return {
    ...(team ? { team } : {}),
    ...(params.get("staff") === "inactive" || params.get("staff") === "all"
      ? { staffStatus: params.get("staff") as CareNavigationStaffStatus }
      : {}),
    criteria: resolveCareNavigationCriteria(overrides),
    month: /^\d{4}-(0[1-9]|1[0-2])$/.test(month) ? month : currentMonth,
    navigators: [
      ...new Set(
        params
          .getAll("cn")
          .map((name) => name.trim())
          .filter(Boolean),
      ),
    ],
  };
}

export function writeDefinitionState(
  search: string,
  state: CareNavigationDefinitionState,
  currentMonth: string,
): string {
  const params = new URLSearchParams(search);
  for (const { id } of CARE_NAVIGATION_CRITERIA) {
    params.delete(`criterion.${id}`);
    if (state.criteria[id] !== DEFAULT_CARE_NAVIGATION_CRITERIA[id]) {
      params.set(`criterion.${id}`, state.criteria[id] ? "1" : "0");
    }
  }
  params.delete("team");
  if (state.team) params.set("team", state.team);
  params.delete("staff");
  if (state.staffStatus && state.staffStatus !== "active") params.set("staff", state.staffStatus);
  params.delete("cn");
  for (const name of [...new Set(state.navigators.map((name) => name.trim()).filter(Boolean))]) {
    params.append("cn", name);
  }
  params.delete("month");
  if (state.month !== currentMonth) params.set("month", state.month);
  return params.toString() ? `?${params}` : "";
}

type CompletionCounts = {
  aligned: number;
  completed: number;
  bookedRemaining: number;
  emailCompleted: number | null;
  combinedCompleted: number | null;
  needsAssessmentDurationMinutes: number;
  needsAssessmentDurationCount: number;
  durationStatus: "available" | "loading" | "unavailable";
  callDurationMinutes: number;
  callDurationCount: number;
  callContactCount: number;
  carePlanUpdated: number;
  carePlanEligible: number;
  reassessmentDenominator: number;
  reassessmentCount: number;
};

type MonthlyCallDuration = NonNullable<
  CareNavigationDefinitionResponse["monthlyCallDurations"]
>[number];
type DurationSummary = Pick<
  CompletionCounts,
  "durationStatus" | "callDurationMinutes" | "callDurationCount" | "callContactCount"
>;

function durationSummary(
  values: Pick<MonthlyCallDuration, "callDurationMinutes" | "callDurationCount"> | undefined,
  status: DurationSummary["durationStatus"],
): DurationSummary {
  return {
    durationStatus: status,
    callDurationMinutes: values?.callDurationMinutes ?? 0,
    callDurationCount: values?.callDurationCount ?? 0,
    callContactCount: values?.callDurationCount ?? 0,
  };
}

export function summarizeMonthlyCallDurations(
  rows: MonthlyCallDuration[],
  selectedMonth: string,
  navigatorUserIds: readonly string[],
  statuses: ReadonlyMap<string, DurationSummary["durationStatus"]>,
) {
  const byMonth = new Map<string, MonthlyCallDuration>();
  const selectedIds = new Set(navigatorUserIds);
  const byAccount = new Map<string, MonthlyCallDuration>();
  for (const row of rows) {
    if (row.navigatorName === null) {
      // The team row deduplicates shared calls, even when account labels collide.
      if (selectedIds.size > 1) byMonth.set(row.monthStart, row);
      continue;
    }
    if (!row.navigatorUserId || !selectedIds.has(row.navigatorUserId)) continue;
    if (selectedIds.size === 1) byMonth.set(row.monthStart, row);
    if (row.monthStart === selectedMonth) {
      byAccount.set(row.navigatorUserId, row);
    }
  }

  // Tables and CSV group by display label. Sum the selected account breakdowns
  // only after identity-based selection, rather than overwriting a shared name.
  const byNavigator = new Map<string, DurationSummary>();
  for (const row of byAccount.values()) {
    const name = row.navigatorName!;
    const total =
      byNavigator.get(name) ??
      durationSummary(undefined, statuses.get(selectedMonth) ?? "available");
    total.callDurationMinutes += row.callDurationMinutes;
    total.callDurationCount += row.callDurationCount;
    total.callContactCount += row.callDurationCount;
    byNavigator.set(name, total);
  }

  const monthStarts = new Set([...statuses.keys(), ...byMonth.keys()]);
  const trend = Array.from(monthStarts)
    .sort()
    .map(
      (monthStart) =>
        [
          monthStart,
          durationSummary(byMonth.get(monthStart), statuses.get(monthStart) ?? "available"),
        ] as const,
    );
  return {
    current: durationSummary(
      byMonth.get(selectedMonth),
      statuses.get(selectedMonth) ?? "available",
    ),
    trend,
    navigators: Array.from(byNavigator),
  };
}

export function summarizeCompletionRows(
  rows: CareNavigationDefinitionResponse["rows"],
  selectedMonth: string,
) {
  const byMonth = new Map<string, CompletionCounts>();
  const byNavigator = new Map<string, CompletionCounts>();
  for (const row of rows) {
    const month = byMonth.get(row.monthStart) ?? {
      aligned: 0,
      completed: 0,
      bookedRemaining: 0,
      emailCompleted: 0,
      combinedCompleted: 0,
      needsAssessmentDurationMinutes: 0,
      needsAssessmentDurationCount: 0,
      durationStatus: "available",
      callDurationMinutes: 0,
      callDurationCount: 0,
      callContactCount: 0,
      carePlanUpdated: 0,
      carePlanEligible: 0,
      reassessmentDenominator: 0,
      reassessmentCount: 0,
    };
    const combined = combinedCompletion(row);
    month.combinedCompleted =
      month.combinedCompleted === null || combined === null
        ? null
        : month.combinedCompleted + combined;
    month.needsAssessmentDurationMinutes += row.needsAssessmentDurationMinutes ?? 0;
    month.needsAssessmentDurationCount += row.needsAssessmentDurationCount ?? 0;
    month.aligned += row.alignedCount;
    month.completed += row.completedCount;
    month.bookedRemaining += row.bookedRemainingCount;
    month.emailCompleted =
      month.emailCompleted === null || row.emailCompletedCount == null
        ? null
        : month.emailCompleted + row.emailCompletedCount;
    if (
      row.durationStatus === "unavailable" ||
      (row.durationStatus === "loading" && month.durationStatus === "available")
    )
      month.durationStatus = row.durationStatus;
    month.callDurationMinutes += row.callDurationMinutes ?? 0;
    month.callDurationCount += row.callDurationCount ?? 0;
    month.callContactCount += row.callContactCount ?? 0;
    month.carePlanUpdated += row.carePlanUpdatedCount;
    month.carePlanEligible += row.carePlanEligibleCount;
    month.reassessmentDenominator += row.reassessmentDenominator;
    month.reassessmentCount += row.reassessmentCount;
    byMonth.set(row.monthStart, month);

    if (row.monthStart === selectedMonth) {
      const navigator = byNavigator.get(row.navigatorName) ?? {
        aligned: 0,
        completed: 0,
        bookedRemaining: 0,
        emailCompleted: 0,
        combinedCompleted: 0,
        needsAssessmentDurationMinutes: 0,
        needsAssessmentDurationCount: 0,
        durationStatus: "available",
        callDurationMinutes: 0,
        callDurationCount: 0,
        callContactCount: 0,
        carePlanUpdated: 0,
        carePlanEligible: 0,
        reassessmentDenominator: 0,
        reassessmentCount: 0,
      };
      navigator.combinedCompleted =
        navigator.combinedCompleted === null || combined === null
          ? null
          : navigator.combinedCompleted + combined;
      navigator.needsAssessmentDurationMinutes += row.needsAssessmentDurationMinutes ?? 0;
      navigator.needsAssessmentDurationCount += row.needsAssessmentDurationCount ?? 0;
      navigator.aligned += row.alignedCount;
      navigator.completed += row.completedCount;
      navigator.bookedRemaining += row.bookedRemainingCount;
      navigator.emailCompleted =
        navigator.emailCompleted === null || row.emailCompletedCount == null
          ? null
          : navigator.emailCompleted + row.emailCompletedCount;
      if (
        row.durationStatus === "unavailable" ||
        (row.durationStatus === "loading" && navigator.durationStatus === "available")
      )
        navigator.durationStatus = row.durationStatus;
      navigator.callDurationMinutes += row.callDurationMinutes ?? 0;
      navigator.callDurationCount += row.callDurationCount ?? 0;
      navigator.callContactCount += row.callContactCount ?? 0;
      navigator.carePlanUpdated += row.carePlanUpdatedCount;
      navigator.carePlanEligible += row.carePlanEligibleCount;
      navigator.reassessmentDenominator += row.reassessmentDenominator;
      navigator.reassessmentCount += row.reassessmentCount;
      byNavigator.set(row.navigatorName, navigator);
    }
  }
  return {
    current: byMonth.get(selectedMonth) ?? {
      aligned: 0,
      completed: 0,
      bookedRemaining: 0,
      emailCompleted: 0,
      combinedCompleted: 0,
      needsAssessmentDurationMinutes: 0,
      needsAssessmentDurationCount: 0,
      durationStatus: "available",
      callDurationMinutes: 0,
      callDurationCount: 0,
      callContactCount: 0,
      carePlanUpdated: 0,
      carePlanEligible: 0,
      reassessmentDenominator: 0,
      reassessmentCount: 0,
    },
    trend: Array.from(byMonth),
    navigators: Array.from(byNavigator),
  };
}

export function summarizeNavigatorAttrition(
  rows: Array<
    Pick<
      CareNavigationDefinitionResponse["rows"][number],
      "monthStart" | "navigatorName" | "assignedCount" | "attritionCount"
    >
  >,
  selectedMonth: string,
) {
  const navigators = new Map<string, { assigned: number; attrited: number } | null>();
  for (const row of rows) {
    if (row.monthStart !== selectedMonth) continue;
    const previous = navigators.get(row.navigatorName);
    if (row.assignedCount === undefined || row.attritionCount === undefined || previous === null) {
      navigators.set(row.navigatorName, null);
      continue;
    }
    navigators.set(row.navigatorName, {
      assigned: (previous?.assigned ?? 0) + row.assignedCount,
      attrited: (previous?.attrited ?? 0) + row.attritionCount,
    });
  }
  return navigators;
}

export function formatNavigatorAttrition(
  value: { assigned: number; attrited: number } | null | undefined,
) {
  return value?.assigned ? `${((value.attrited / value.assigned) * 100).toFixed(1)}%` : "—";
}

export type CompletionChannel = "calls" | "emails" | "combined";

export function combinedCompletion(row: CareNavigationDefinitionResponse["rows"][number]) {
  const calls = new Set(row.recordIds?.completed ?? []);
  const emails = new Set(row.recordIds?.emailCompleted ?? []);
  if (
    row.emailCompletedCount == null ||
    calls.size !== row.completedCount ||
    emails.size !== row.emailCompletedCount
  )
    return null;
  return new Set([...calls, ...emails]).size;
}

export function completionChannelCount(counts: CompletionCounts, channel: CompletionChannel) {
  return channel === "calls"
    ? counts.completed
    : channel === "emails"
      ? counts.emailCompleted
      : counts.combinedCompleted;
}
